import Keycloak, { KeycloakInitOptions, KeycloakTokenParsed } from 'keycloak-js';
import type { TokenProvider, KeycloakConfig } from './types.js';

export interface SlopeKeycloakOptions {
  config: KeycloakConfig;
  tokenProvider?: TokenProvider;
  initOptions?: Partial<KeycloakInitOptions>;
}

export class SlopeKeycloak {
  private keycloak: Keycloak;
  private tokenProvider?: TokenProvider;

  constructor(options: SlopeKeycloakOptions) {
    this.keycloak = new Keycloak(options.config);
    this.tokenProvider = options.tokenProvider;

    // Set default init options
    if (options.initOptions) {
      Object.assign(this.initOptions, options.initOptions);
    }
  }

  private initOptions: KeycloakInitOptions = {
    onLoad: 'check-sso',
    flow: 'standard',
    pkceMethod: 'S256',
    silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
    checkLoginIframe: true,
    checkLoginIframeInterval: 30,
    enableLogging: true,
    redirectUri: window.location.href,
    scope: 'audience',
  };

  get instance(): Keycloak {
    return this.keycloak;
  }

  async init(): Promise<boolean> {
    if (this.tokenProvider?.isClientCredentials()) {
      return true; // skip PKCE when secret is present server-side
    }

    return this.keycloak.init(this.initOptions);
  }

  async getToken(): Promise<{
    token: string;
    tokenType: KeycloakTokenParsed['typ'] | 'Bearer';
  }> {
    if (this.tokenProvider?.isClientCredentials()) {
      const token = await this.tokenProvider.getDevToken();
      return { token, tokenType: 'Bearer' as const };
    }

    if (!this.keycloak.didInitialize) {
      throw new Error('SlopeKeycloak: Keycloak is not initialized. Call init() first.');
    }

    if (!this.keycloak.authenticated || !this.keycloak.token) {
      this.keycloak.updateToken(30).catch(async () => {
        console.log('SlopeKeycloak: Token refresh failed, redirecting to login');
        await this.keycloak.login({
          redirectUri: window.location.href,
        });
      });
    }

    return {
      token: this.keycloak.token!,
      tokenType: (this.keycloak.tokenParsed as KeycloakTokenParsed & { typ?: string })
        ?.typ as KeycloakTokenParsed['typ'],
    };
  }

  setupEventHandlers(handlers: {
    onAuthSuccess?: () => void;
    onAuthError?: (error: unknown) => void;
    onAuthLogout?: () => void;
    onTokenExpired?: () => Promise<void>;
  }) {
    if (handlers.onAuthSuccess) {
      this.keycloak.onAuthSuccess = handlers.onAuthSuccess;
    }
    if (handlers.onAuthError) {
      this.keycloak.onAuthError = handlers.onAuthError;
    }
    if (handlers.onAuthLogout) {
      this.keycloak.onAuthLogout = handlers.onAuthLogout;
    }
    if (handlers.onTokenExpired) {
      this.keycloak.onTokenExpired = handlers.onTokenExpired;
    }
  }
}
